import { z } from 'zod';

/**
 * Contrato do `status.json` publicado pelo Nicon no bucket.
 *
 * Este schema é a fronteira de confiança da página: tudo que entra passa por aqui.
 * Um arquivo malformado precisa cair no caminho de erro em vez de renderizar meia
 * tela com campos indefinidos.
 *
 * O contrato também é uma garantia de privacidade estrutural — não existe campo para
 * nome de servidor, tamanho de fila, exceção ou dado de cliente. O que não tem campo
 * não vaza por descuido.
 */

export const VERSAO_CONTRATO_SUPORTADA = 1;

export const EstadoServicoSchema = z.enum([
  'operacional',
  'degradado',
  'indisponivel',
  'manutencao',
  'desconhecido',
]);

export const EstadoIncidenteSchema = z.enum([
  'investigando',
  'identificado',
  'monitorando',
  'resolvido',
]);

const DataHoraSchema = z.iso.datetime({ offset: true });
const DataSchema = z.iso.date();

export const DiaDisponibilidadeSchema = z.object({
  data: DataSchema,
  estado: EstadoServicoSchema,
});

export const ServicoSchema = z.object({
  chave: z.string().min(1),
  nome: z.string().min(1),
  descricao: z.string().default(''),
  estado: EstadoServicoSchema,
  /** Percentual de disponibilidade na janela. */
  disponibilidade: z.number().min(0).max(100),
  /** Ordenado do dia mais antigo para hoje. */
  dias: z.array(DiaDisponibilidadeSchema).default([]),
});

export const AtualizacaoIncidenteSchema = z.object({
  em: DataHoraSchema,
  estado: EstadoIncidenteSchema,
  texto: z.string().min(1),
});

export const IncidenteAtivoSchema = z.object({
  id: z.string().min(1),
  titulo: z.string().min(1),
  estado: EstadoIncidenteSchema,
  iniciado_em: DataHoraSchema,
  resumo: z.string().default(''),
  servicos_afetados: z.array(z.string()).default([]),
  /** Ordenado da atualização mais recente para a mais antiga. */
  atualizacoes: z.array(AtualizacaoIncidenteSchema).default([]),
});

export const ManutencaoSchema = z.object({
  id: z.string().min(1),
  titulo: z.string().min(1),
  descricao: z.string().default(''),
  inicio: DataHoraSchema,
  fim: DataHoraSchema,
});

export const IncidenteHistoricoSchema = z.object({
  id: z.string().min(1),
  titulo: z.string().min(1),
  resumo: z.string().default(''),
  estado: z.enum(['resolvido', 'concluida']),
  iniciado_em: DataHoraSchema,
  resolvido_em: DataHoraSchema.nullable().default(null),
  duracao_minutos: z.number().int().min(0).nullable().default(null),
});

export const SuporteSchema = z.object({
  telefone: z.string().nullable().default(null),
  email: z.string().nullable().default(null),
});

export const StatusPublicoSchema = z.object({
  versao: z.number().int(),
  gerado_em: DataHoraSchema,
  estado_geral: EstadoServicoSchema,
  janela_dias: z.number().int().min(1).max(365).default(90),
  servicos: z.array(ServicoSchema).default([]),
  incidentes_ativos: z.array(IncidenteAtivoSchema).default([]),
  manutencoes: z.array(ManutencaoSchema).default([]),
  historico: z.array(IncidenteHistoricoSchema).default([]),
  suporte: SuporteSchema.default({ telefone: null, email: null }),
});

export type ResultadoValidacao =
  | { ok: true; dados: z.infer<typeof StatusPublicoSchema> }
  | { ok: false; motivo: string };

/**
 * Valida um payload cru vindo da rede.
 *
 * Trata versão incompatível como falha explícita: uma página velha lendo um contrato
 * novo produziria uma tela silenciosamente errada, que é o pior desfecho possível
 * numa página de status.
 */
export function validarStatusPublico(payload: unknown): ResultadoValidacao {
  const resultado = StatusPublicoSchema.safeParse(payload);

  if (!resultado.success) {
    const primeiro = resultado.error.issues[0];
    const caminho = primeiro?.path.join('.') || 'raiz';

    return { ok: false, motivo: `Contrato inválido em "${caminho}": ${primeiro?.message}` };
  }

  if (resultado.data.versao !== VERSAO_CONTRATO_SUPORTADA) {
    return {
      ok: false,
      motivo: `Versão de contrato não suportada: ${resultado.data.versao} (esperada ${VERSAO_CONTRATO_SUPORTADA})`,
    };
  }

  return { ok: true, dados: resultado.data };
}
